/**
 * The Calendar's month grid (piece 12, the Calendar page). Pure arithmetic over
 * days and events, so the rules are testable without a browser: the month
 * matrix, the chips a day cell carries, and the bar a multi-day event draws.
 *
 * 🔨 The grid is laid out in the viewer's own zone, never the stored UTC date.
 * A late evening belongs to the day the viewer is living, and a chip whose
 * event is held elsewhere names that zone rather than being translated away.
 */
import { breakpoints } from '../breakpoints';
import { addCalendarDays, calendarDayInZone, wallClockInZone } from '../zonedTime';

/** 🔨 Two chips per day cell, and the rest become an overflow count (piece 12). */
export const CHIPS_PER_DAY = 2;

export const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

export interface GridEvent {
  id: string;
  subject: string;
  eventType: string;
  /** The stored instant, as an ISO string. */
  startsAt: string;
  endsAt?: string | null;
  /** The event's own zone, named on a chip wherever it differs from the viewer's. */
  timeZone: string;
  cancelledAt?: string | null;
  /** Whose calendar the event is on; an overlaid report's events carry their owner's own colour (piece 12, Team View). */
  ownerPersonId?: string | null;
  /** Set on another person's event in Overlay: the short name the chip leads with, "R. Springer: Field Visit". */
  ownerLabel?: string | null;
  /** 🔨 Drawn as its own block before the event on the week view, never folded into it (piece 12). */
  travelBufferMinutes?: number | null;
  /** What a week block tall enough to hold it shows beneath its title: the place, the notes and the attendees (product owner, 2026-09-10). */
  place?: string | null;
  meetingLink?: string | null;
  callNumber?: string | null;
  notes?: string | null;
  attendees?: readonly { displayName: string; avatarColor: string; attending: string }[];
}

/** How much of itself a week block shows, by the minutes it spans: the title alone, then the time and place, then the notes and attendees. */
export function blockDetail(startMinute: number, endMinute: number): 0 | 1 | 2 {
  const minutes = endMinute - startMinute;
  if (minutes >= 105) return 2;
  if (minutes >= 50) return 1;
  return 0;
}

export interface GridDay {
  /** The day as YYYY-MM-DD in the viewer's zone. */
  date: string;
  dayOfMonth: number;
  inMonth: boolean;
  isToday: boolean;
}

/** A run of one event across one week's row: where it starts, how far it reaches, and whether it is cut. */
export interface GridSegment {
  event: GridEvent;
  /** 0-6, the column the run starts in. */
  column: number;
  /** How many columns the run covers, at least 1. */
  span: number;
  /** True when the event began before this row's first day. */
  continuesBefore: boolean;
  /** True when the event runs past this row's last day. */
  continuesAfter: boolean;
  /** Which stacked line the bar sits on, 0-based, within the cap. */
  row: number;
}

export interface GridWeek {
  days: GridDay[];
  segments: GridSegment[];
  /** How many events a given date holds beyond the cap, keyed by date. */
  overflow: Record<string, number>;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Sunday of the week a date falls in, which is the column the grid starts from. */
export function weekStart(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return addCalendarDays(date, -new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1)).getUTCDay());
}

/**
 * The six weeks a month grid draws. Six rather than a variable count, so the
 * grid does not change height as the reader pages through the year.
 */
export function monthDays(monthStart: string, today: string): GridDay[][] {
  const month = monthStart.slice(0, 7);
  let cursor = weekStart(`${month}-01`);
  const weeks: GridDay[][] = [];
  for (let week = 0; week < 6; week += 1) {
    const days: GridDay[] = [];
    for (let column = 0; column < 7; column += 1) {
      days.push({
        date: cursor,
        dayOfMonth: Number(cursor.slice(8)),
        inMonth: cursor.slice(0, 7) === month,
        isToday: cursor === today,
      });
      cursor = addCalendarDays(cursor, 1);
    }
    weeks.push(days);
  }
  return weeks;
}

/** The last day an event covers: its end, or its start where it has no end. */
export function lastDayOf(event: GridEvent, zone: string): string {
  if (!event.endsAt) return calendarDayInZone(event.startsAt, zone);
  // An event ending exactly at midnight belongs to the day before, not to the
  // day it touches for an instant.
  const end = new Date(event.endsAt);
  const wall = wallClockInZone(event.endsAt, zone);
  const endsAtMidnight = wall.slice(11) === '00:00';
  return calendarDayInZone(endsAtMidnight ? new Date(end.getTime() - 1).toISOString() : event.endsAt, zone);
}

/**
 * One month, laid out. 🔨 A multi-day event is one continuous bar across every
 * cell it covers, labelled once and carrying a continuation arrow, wrapped one
 * segment per week — never a separate chip per day (piece 12).
 */
export function layOutMonth(monthStart: string, events: readonly GridEvent[], zone: string, today: string): GridWeek[] {
  const weeks = monthDays(monthStart, today);
  const ordered = [...events].sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  return weeks.map((days) => {
    const first = days[0]!.date;
    const last = days[6]!.date;
    const segments: GridSegment[] = [];
    const overflow: Record<string, number> = {};
    /** Which stacked lines each day has already given away, so nothing lands on top of anything. */
    const taken = new Map<string, Set<number>>();
    const linesOf = (date: string): Set<number> => {
      const held = taken.get(date);
      if (held) return held;
      const made = new Set<number>();
      taken.set(date, made);
      return made;
    };

    const runs = ordered.flatMap((event) => {
      const from = calendarDayInZone(event.startsAt, zone);
      const to = lastDayOf(event, zone);
      if (to < first || from > last) return [];
      const runsFrom = from < first ? first : from;
      const runsTo = to > last ? last : to;
      const span = Math.round((Date.parse(`${runsTo}T00:00:00Z`) - Date.parse(`${runsFrom}T00:00:00Z`)) / DAY_MS) + 1;
      const dates = Array.from({ length: span }, (_, step) => addCalendarDays(runsFrom, step));
      return [{ event, column: days.findIndex((day) => day.date === runsFrom), span, dates, continuesBefore: from < first, continuesAfter: to > last }];
    });

    /**
     * 🔨 A run that crosses more than one day is laid before any single day's
     * own chips, and it takes the line that is free on EVERY day it covers.
     * 💰 The bar took the line its first day had free and was dropped outright
     * when that day was full, so a week-long event vanished from the six cells
     * it crossed because the Monday it started on already held two meetings
     * (product owner, 2026-09-20).
     */
    for (const run of [...runs.filter((row) => row.span > 1), ...runs.filter((row) => row.span === 1)]) {
      let line: number | null = null;
      for (let candidate = 0; candidate < CHIPS_PER_DAY; candidate += 1) {
        if (run.dates.every((date) => !linesOf(date).has(candidate))) {
          line = candidate;
          break;
        }
      }
      if (line === null) {
        // Nothing free on one of its days: the run is counted as overflow on every day it covers.
        for (const date of run.dates) overflow[date] = (overflow[date] ?? 0) + 1;
        continue;
      }
      for (const date of run.dates) linesOf(date).add(line);
      segments.push({ event: run.event, column: run.column, span: run.span, continuesBefore: run.continuesBefore, continuesAfter: run.continuesAfter, row: line });
    }

    return { days, segments, overflow };
  });
}

/**
 * The colour family an event type draws in. 🔨 One family per event type, so
 * the five types of piece 2 read as five colours and the neutral one means only
 * *no type we know*. 💰 Four families served five types, so approved time off
 * and a work order's blocked time — the two multi-day kinds — both drew in the
 * neutral grey and the legend named neither (product owner, 2026-09-20).
 */
export type ChipKind = 'inPerson' | 'online' | 'call' | 'pto' | 'workOrder' | 'none';

export function chipKind(eventType: string): ChipKind {
  if (eventType === 'Physical') return 'inPerson';
  if (eventType === 'Online') return 'online';
  if (eventType === 'Phone') return 'call';
  if (eventType === 'PTO') return 'pto';
  if (eventType === 'WorkOrderBlock') return 'workOrder';
  return 'none';
}

/**
 * A chip's label: the time it starts, then its subject. 🔨 An event held in a
 * zone that is not the viewer's names that zone, because a nine o'clock
 * meeting shown as nine o'clock is a missed meeting (piece 12).
 */
export function chipLabel(event: GridEvent, zone: string, continuesBefore: boolean): string {
  const time = continuesBefore ? '' : `${clockOf(event.startsAt, zone)} `;
  const elsewhere = event.timeZone && event.timeZone !== zone ? ` (${event.timeZone})` : '';
  const owner = event.ownerLabel ? `${event.ownerLabel}: ` : '';
  return `${time}${owner}${event.subject}${elsewhere}`;
}

/** A start time as a reader scans it: 9:00, 10:00 PM, dropping a zero minute is not done. */
export function clockOf(instant: string, zone: string): string {
  const [hh, mm] = wallClockInZone(instant, zone).slice(11).split(':');
  const hour = Number(hh);
  const suffix = hour < 12 ? 'AM' : 'PM';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${mm} ${suffix}`;
}

/** The month strip's five entries, the chosen one in the middle. */
export function monthStrip(monthStart: string): string[] {
  return [-2, -1, 0, 1, 2].map((offset) => shiftMonth(monthStart, offset));
}

export function shiftMonth(monthStart: string, by: number): string {
  const [y, m] = monthStart.split('-').map(Number);
  const date = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1 + by, 1));
  return date.toISOString().slice(0, 7) + '-01';
}

export function monthName(monthStart: string, long = false): string {
  const date = new Date(`${monthStart.slice(0, 7)}-01T00:00:00Z`);
  return new Intl.DateTimeFormat('en-US', { month: long ? 'long' : 'short', timeZone: 'UTC' }).format(date);
}

// ---- The week view: hourly slots (piece 12) ----

/** The hours a week column shows by default; a day with an event outside them widens the range to hold it. */
/**
 * The window the week opens scrolled to. 🔨 The week draws the whole day, so
 * these bound what is in view at first and never what exists. 💰 They bounded
 * the drawn range, so a day ran 6 AM to 8 PM and there was no way to reach an
 * earlier or later hour at all (product owner, 2026-09-14).
 */
export const WEEK_HOURS_FROM = 6;
export const WEEK_HOURS_TO = 20;

/** 🔒 A week draws midnight to midnight, so every hour of the day is reachable. */
export const DAY_HOURS_FROM = 0;
export const DAY_HOURS_TO = 24;
/** An event with no end is drawn an hour long, which is what the modal defaults an end to. */
export const DEFAULT_EVENT_MINUTES = 60;

export interface WeekBlock {
  event: GridEvent;
  /** 0-6, the day column. */
  column: number;
  /** Minutes from the column's own midnight, in the viewer's zone. */
  startMinute: number;
  endMinute: number;
  /** 🔨 The Travel Buffer is a block of its own, immediately before the event's, with its own label (piece 12). */
  kind: 'event' | 'travel';
  continuesBefore: boolean;
  continuesAfter: boolean;
}

/** A multi-day event on the week: one band per day it covers, across the top of the columns, never a full-height block (decision 0296). */
export interface WeekBand {
  event: GridEvent;
  column: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export interface GridWeekHours {
  days: GridDay[];
  blocks: WeekBlock[];
  bands: WeekBand[];
  /** The first and last hour the columns draw, whole hours. */
  fromHour: number;
  toHour: number;
}

function minuteOf(wall: string): number {
  const [hh, mm] = wall.slice(11).split(':').map(Number);
  return (hh ?? 0) * 60 + (mm ?? 0);
}

/** Seven days from a Sunday, with the events of each laid out by the minute in the viewer's zone. */
export function layOutWeek(start: string, events: readonly GridEvent[], zone: string, today: string): GridWeekHours {
  const days: GridDay[] = [];
  for (let column = 0; column < 7; column += 1) {
    const date = addCalendarDays(start, column);
    days.push({ date, dayOfMonth: Number(date.slice(8)), inMonth: true, isToday: date === today });
  }
  const last = days[6]!.date;
  const blocks: WeekBlock[] = [];
  const bands: WeekBand[] = [];
  const ordered = [...events].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  for (const event of ordered) {
    const startWall = wallClockInZone(event.startsAt, zone);
    const endInstant = event.endsAt ?? new Date(Date.parse(event.startsAt) + DEFAULT_EVENT_MINUTES * 60_000).toISOString();
    const endWall = wallClockInZone(endInstant, zone);
    const from = startWall.slice(0, 10);
    const to = lastDayOf({ ...event, endsAt: endInstant }, zone);
    if (to < start || from > last) continue;
    // 🔨 An event covering more than one day is a band across the top of its days, as the month draws it; a block a column tall reads as a wall (decision 0296).
    if (to > from) {
      for (let column = 0; column < 7; column += 1) {
        const date = days[column]!.date;
        if (date < from || date > to) continue;
        bands.push({ event, column, continuesBefore: date !== from, continuesAfter: date !== to });
      }
      continue;
    }
    for (let column = 0; column < 7; column += 1) {
      const date = days[column]!.date;
      if (date < from || date > to) continue;
      const startMinute = date === from ? minuteOf(startWall) : 0;
      // An end at midnight closes the previous day at its last minute.
      const endsOnThisDay = date === endWall.slice(0, 10) && minuteOf(endWall) > 0;
      const endMinute = endsOnThisDay ? minuteOf(endWall) : 24 * 60;
      blocks.push({ event, column, startMinute, endMinute: Math.max(endMinute, startMinute + 1), kind: 'event', continuesBefore: date !== from, continuesAfter: date !== to });
      if (date === from && event.travelBufferMinutes && event.travelBufferMinutes > 0) {
        blocks.push({ event, column, startMinute: Math.max(0, startMinute - event.travelBufferMinutes), endMinute: startMinute, kind: 'travel', continuesBefore: false, continuesAfter: false });
      }
    }
  }
  // The whole day is drawn; the grid scrolls to the working window on arrival.
  return { days, blocks, bands, fromHour: DAY_HOURS_FROM, toHour: DAY_HOURS_TO };
}

/** Where a block sits in its column, as fractions of the drawn range, so the grid places it without knowing pixels. */
export function blockPlacement(block: WeekBlock, fromHour: number, toHour: number): { top: number; height: number } {
  const range = (toHour - fromHour) * 60;
  const top = (block.startMinute - fromHour * 60) / range;
  const height = (block.endMinute - block.startMinute) / range;
  return { top: Math.max(0, top), height: Math.max(height, 1 / range) };
}

/** A travel block's own label: Travel · 30 min. */
export function travelLabel(minutes: number, word: string): string {
  return `${word} · ${minutes} min`;
}

export function shiftWeek(start: string, by: number): string {
  return addCalendarDays(start, by * 7);
}

/** The hour labels down a week's left edge: 6 AM, 7 AM … */
export function hourLabel(hour: number): string {
  if (hour === 0 || hour === 24) return '12 AM';
  if (hour === 12) return '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

/** How many dots a compact day cell draws before it counts the rest (piece 12, the calendar on a phone; decision 0266). */
export const COMPACT_DOTS = 3;

/**
 * The content region height under which the calendar is compact (decision
 * 0329), beside the CSS token `--calendar-compact-height`. Six month rows at
 * their minimum with the rows above them need about this much; shorter, the
 * dots and the day list read where two chips a day cannot.
 */
export const CALENDAR_COMPACT_HEIGHT_PX = 560;

/**
 * Whether the calendar draws its compact body: under the `sm` breakpoint in
 * width (decision 0266) or under the compact height (decision 0329). An
 * unmeasured dimension decides nothing, so the first paint is the full grid.
 */
export function calendarCompact(width: number | null, height: number | null): boolean {
  if (width !== null && width < breakpoints.sm) return true;
  return height !== null && height < CALENDAR_COMPACT_HEIGHT_PX;
}

/** The events that touch one day in the viewer's zone, a multi-day event among them on every day it covers, in start order. */
export function eventsOnDay(events: readonly GridEvent[], date: string, zone: string): GridEvent[] {
  return events
    .filter((event) => calendarDayInZone(event.startsAt, zone) <= date && lastDayOf(event, zone) >= date)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/** A box in the coordinates of whatever the Expanded card is positioned inside. */
export interface AnchorBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The gap between the chip and the card anchored beside it, beside the CSS
 * token `--space-2`.
 */
export const CALENDAR_CARD_GAP_PX = 8;

/**
 * Where the Expanded card sits, in the coordinates of the box it is positioned
 * inside: beside the chip it was opened from, on whichever side has room, and
 * never with an edge outside the part of the grid on show.
 *
 * 💰 The card was placed from the day's column number alone, so in the week it
 * opened at the top of the day column however far down the day the block sat,
 * and the week now draws midnight to midnight (product owner, 2026-09-14).
 */
export function anchorCard(chip: AnchorBox, shown: AnchorBox, card: { width: number; height: number }, gap = CALENDAR_CARD_GAP_PX): { left: number; top: number } {
  const beside = chip.left + chip.width + gap;
  const before = chip.left - card.width - gap;
  const rightEdge = shown.left + shown.width;
  const fitsBeside = beside + card.width <= rightEdge;
  const left = fitsBeside ? beside : before >= shown.left ? before : holdBetween(beside, shown.left, rightEdge - card.width);
  return { left, top: holdBetween(chip.top, shown.top, shown.top + shown.height - card.height) };
}

/** A figure held inside a range; a range narrower than the card pins it to the near edge. */
function holdBetween(value: number, low: number, high: number): number {
  if (high <= low) return low;
  return Math.min(Math.max(value, low), high);
}
